import isEqual from 'lodash/isEqual';
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useState,
} from 'react';

import type { ChartNames } from '@revelio/d3';

import type { DataProviderSet } from '../../DataProvider/types';

export interface DownloadState {
  // For generic charts using DataProviderSet
  dataProviderSet: DataProviderSet | null;
  // For specialized charts with custom data structures
  customData: unknown | null;
  customDataType: string | null;
}

interface ChartMenuContextValue {
  //
  // Download Data Functionality
  //
  downloadState: DownloadState;
  setDataProviderSet: (dataProviderSet: DataProviderSet) => void;
  setCustomData: (data: unknown, dataType: string) => void;
  //
  // Plot Link Functionality
  //
  shareState: ShareState;
  setShareData: (state: ShareState) => void;
}

const ChartMenuContext = createContext<ChartMenuContextValue | undefined>(
  undefined
);

export type ShareState = {
  chartType?: ChartNames;
  chartProps?: Record<string, unknown>;
  data?: unknown;
};

export const ChartMenuDataProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const [shareState, setShareState] = useState<ShareState>({});
  const [downloadState, setDownloadState] = useState<DownloadState>({
    dataProviderSet: null,
    customData: null,
    customDataType: null,
  });

  const setDataProviderSet = useCallback(
    (dataProviderSet: DataProviderSet) => {
      if (!isEqual(downloadState.dataProviderSet, dataProviderSet)) {
        setDownloadState({
          dataProviderSet,
          customData: null,
          customDataType: null,
        });
      }
    },
    [downloadState.dataProviderSet]
  );

  const setCustomData = useCallback(
    (data: unknown, dataType: string) => {
      if (
        !isEqual(downloadState.customData, data) ||
        downloadState.customDataType !== dataType
      ) {
        setDownloadState({
          dataProviderSet: null,
          customData: data,
          customDataType: dataType,
        });
      }
    },
    [downloadState.customData, downloadState.customDataType]
  );

  const setShareData = useCallback((state: ShareState) => {
    // Shallow-clone chartProps and remove any function-valued entries. These
    // cannot be serialized when generating a share link.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    // biome-ignore lint/suspicious/noExplicitAny: migrated from develop
    const chartProps = state.chartProps ?? ({} as Record<string, any>);

    // Identify keys whose values are functions.
    const functionKeys = Object.keys(chartProps).filter(
      (key) => typeof chartProps[key] === 'function'
    );

    let sanitizedChartProps = chartProps;
    if (functionKeys.length) {
      // Remove those keys from a shallow copy so we don't mutate the original
      // object provided by the caller.
      sanitizedChartProps = { ...chartProps };
      // biome-ignore lint/suspicious/useIterableCallbackReturn: migrated from develop
      functionKeys.forEach((key) => delete sanitizedChartProps[key]);
    }

    const sanitizedState: ShareState = {
      ...state,
      chartProps: sanitizedChartProps,
    };

    setShareState((prev) =>
      isEqual(prev, sanitizedState) ? prev : sanitizedState
    );
  }, []);

  return (
    <ChartMenuContext.Provider
      value={{
        downloadState,
        setDataProviderSet,
        setCustomData,
        shareState,
        setShareData,
      }}
    >
      {children}
    </ChartMenuContext.Provider>
  );
};

export const useChartMenuContext = (): ChartMenuContextValue => {
  const context = useContext(ChartMenuContext);
  if (context === undefined) {
    throw new Error(
      'useChartDownloadContext must be used within a <ChartMenuDataProvider />'
    );
  }
  return context;
};
